import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Container, Sprite } from '@pixi/react';
import { textureChangeFrame } from '../../../core/helpers/textureChangeFrame';

import { menuSectionStore } from '../../../store/menuSectionStore';
import { getGameImages } from '../../../core/helpers/getGameImages';
import { MenuIconsId } from '../../../core/constants/menuIconsId';
import { isMobile } from '../../../core/helpers/isMobile';
import { iframeService } from '../../../services/iframeService';
import { PopupTypes } from '../../../core/constants/popupTypes';
import { applicationStore } from '../../../store/applicationStore';
import {userSettingsStore} from "../../../store/userSettingsStore";
import {soundService} from "../../../services/soundService";

const MenuIcons = observer(({ x = 0, y, margin }) => {
  const { isDemo } = applicationStore;
  const { selectedIconId, setSelectedIconId, setIsShowMenuPopup } = menuSectionStore;
  const {
    homeIconHover,
    homeIconDefault,
    homeIconDisable,
    homeIconSelected,
    homeIconDefaultBig,
    homeIconSelectedBig,
    payTableIconHover,
    payTableIconDefault,
    payTableIconDisable,
    payTableIconSelected,
    payTableIconDefaultBig,
    payTableIconSelectedBig,
    rulesIconHover,
    rulesIconDefault,
    rulesIconDisable,
    rulesIconSelected,
    rulesIconDefaultBig,
    rulesIconSelectedBig,
    historyIconHover,
    historyIconDefault,
    historyIconDisable,
    historyIconSelected,
  } = useMemo(() => {
    return getGameImages();
  }, []);

  const menuNavigationIcons = useMemo(() => {
    const navIcons = [
      {
        id: MenuIconsId.Home,
        image: {
          default: isDemo ? (isMobile() ? homeIconDefaultBig : homeIconDefault) : homeIconDefault,
          hover: homeIconHover,
          disable: homeIconDisable,
          selected: isDemo
            ? isMobile()
              ? homeIconSelectedBig
              : homeIconSelected
            : homeIconSelected,
        },
      },
      {
        id: MenuIconsId.PayTable,
        image: {
          default: isDemo
            ? isMobile()
              ? payTableIconDefaultBig
              : payTableIconDefault
            : payTableIconDefault,
          hover: payTableIconHover,
          selected: isDemo
            ? isMobile()
              ? payTableIconSelectedBig
              : payTableIconSelected
            : payTableIconSelected,
          disable: payTableIconDisable,
        },
      },
      {
        id: MenuIconsId.GameHistory,
        image: {
          default: historyIconDefault,
          hover: historyIconHover,
          selected: historyIconSelected,
          disable: historyIconDisable,
        },
      },
      {
        id: MenuIconsId.GameRules,
        image: {
          default: isDemo
            ? isMobile()
              ? rulesIconDefaultBig
              : rulesIconDefault
            : rulesIconDefault,
          hover: rulesIconHover,
          selected: isDemo
            ? isMobile()
              ? rulesIconSelectedBig
              : rulesIconSelected
            : rulesIconSelected,
          disable: rulesIconDisable,
        },
      },
    ];

    if (isDemo) {
      navIcons.splice(2, 1);
    }

    return navIcons;
  }, [
    isDemo,
    homeIconDefaultBig,
    homeIconDefault,
    homeIconHover,
    homeIconDisable,
    homeIconSelectedBig,
    homeIconSelected,
    payTableIconDefaultBig,
    payTableIconDefault,
    payTableIconHover,
    payTableIconSelectedBig,
    payTableIconSelected,
    payTableIconDisable,
    historyIconDefault,
    historyIconHover,
    historyIconSelected,
    historyIconDisable,
    rulesIconDefaultBig,
    rulesIconDefault,
    rulesIconHover,
    rulesIconSelectedBig,
    rulesIconSelected,
    rulesIconDisable,
  ]);

  const handlePointerover = useCallback(
    (e) => {
      const { id } = e.target;
      if (id === selectedIconId) return;
      textureChangeFrame(e.target, menuNavigationIcons.find((el) => el.id === id).image.hover);
    },
    [selectedIconId, menuNavigationIcons]
  );

  const handlePointerOut = useCallback(
    (e) => {
      const { id } = e.target;
      if (id === selectedIconId) return;
      textureChangeFrame(e.target, menuNavigationIcons.find((el) => el.id === id).image.default);
    },
    [selectedIconId, menuNavigationIcons]
  );

  const handlePointerUp = useCallback(
    (e) => {
      const { id } = e.target;
      if (id === MenuIconsId.Home) {
        iframeService.redirectHome();
      } else if (id === MenuIconsId.GameHistory) {
        userSettingsStore.setIsSoundOn(false);
        soundService.stopAll();
        setIsShowMenuPopup(false);
        window.parent.postMessage(
          { name: PopupTypes.OPEN_SLOT_HISTORY, payload: { type: 'lines' } },
          '*'
        );
      } else {
        setSelectedIconId(id);
        textureChangeFrame(e.target, menuNavigationIcons.find((el) => el.id === id).image.selected);
      }
    },
    [setSelectedIconId, menuNavigationIcons]
  );
  return (
    <Container name='MenuIconsContainer' x={x} y={y}>
      {(isMobile() ? menuNavigationIcons : menuNavigationIcons.slice(1)).map(
        ({ image, id }, index) => {
          return (
            <Sprite
              id={id}
              key={id}
              anchor={0.5}
              interactive
              cursor='pointer'
              y={isMobile() ? 0 : index * margin}
              x={isMobile() ? index * margin : 0}
              texture={id === selectedIconId ? image.selected : image.default}
              pointerup={handlePointerUp}
              pointerover={handlePointerover}
              pointerout={handlePointerOut}
            />
          );
        }
      )}
    </Container>
  );
});

export default MenuIcons;
